/// <amd-module name="Build/SettingsToggle/SettingsToggle" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { useComponentContext } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Link } from "VSSUI/Link";
import { Pill, PillVariant } from "VSSUI/Pill";
import { Toggle } from "VSSUI/Toggle";
import { ITooltipProps, Tooltip } from "VSSUI/TooltipEx";

import * as Resources from "Build/SettingsToggle/Resources";

const baseToggleWidth = 96;
const baseTextWidth = 30;
const averageCharWidth = 10;

const TelemetryArea = "Build";
const TelemetryFeature = "SettingsToggle";

export interface SettingsToggleProps {
  title: string;
  description: string | React.ReactNode;

  learnMoreLink?: string;

  checked?: boolean;
  auditChecked?: boolean;

  disabled?: boolean;
  auditDisabled?: boolean;

  toggleClassName?: string;
  toggleTooltipProps?: ITooltipProps;
  toggleAuditTooltipProps?: ITooltipProps;

  onChange?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => void;
  onAuditChange?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, auditChecked: boolean) => void;

  isHighRisk?: boolean;
  isHighRiskAudit?: boolean;
  highRiskTitle?: string;
}

export const SettingsToggle = (props: SettingsToggleProps) => {
  const toggleTextLength = Math.max(Resources.ToggleOn.length, Resources.ToggleOff.length);
  const textWidth = toggleTextLength * averageCharWidth;
  const currentTextWidth = baseTextWidth > textWidth ? 0 : textWidth;
  const currentWidth = currentTextWidth ? baseToggleWidth - baseTextWidth + currentTextWidth : 0;

  const auditToggleTextLength = Math.max(Resources.HighRiskAuditToggleOff.length, Resources.HighRiskAuditToggleOn.length);
  const auditTextWidth = auditToggleTextLength * averageCharWidth;
  const auditCrrentTextWidth = baseTextWidth > auditTextWidth ? 0 : auditTextWidth;
  const auditCurrentWidth = auditCrrentTextWidth ? baseToggleWidth - baseTextWidth + auditCrrentTextWidth : 0;

  const toggleClassName = props.toggleClassName ?? "pipelines-settings-toggle";

  const { checked, auditChecked, description, disabled, auditDisabled, isHighRisk, isHighRiskAudit, highRiskTitle, learnMoreLink, onChange, onAuditChange, title, toggleTooltipProps, toggleAuditTooltipProps } = props;
  const showHighRiskPill = isHighRisk && !checked;
  const showHighRiskAudit = isHighRisk && isHighRiskAudit && !checked; // Only show audit mode for high risk when settings is not checked.
  const showHighRiskTitle = isHighRisk && !checked;

  const context = useComponentContext();
  const telemetryService = context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");

  const publishTelemetry = (properties: { [key: string]: any }) => {
    telemetryService.publishEvent(TelemetryArea, TelemetryFeature, properties);
  };

  const onLearnMoreClicked = () => {
    publishTelemetry({
      "action": "LearnMoreClicked",
      "target": learnMoreLink,
      "isHighRiskDisplayed": showHighRiskPill
    });
  };

  return (
    <div className="flex-row padding-vertical-16 padding-horizontal-20">
      <div className="flex-column margin-right-16 padding-right-16 padding-top-4" style={currentWidth ? { minWidth: currentWidth } : undefined}>
        <Toggle
          ariaLabel={format(Resources.ToggleFormat, title, checked ? Resources.ToggleOn : Resources.ToggleOff)}
          className={toggleClassName}
          checked={checked}
          disabled={disabled}
          offText={Resources.ToggleOff}
          onText={Resources.ToggleOn}
          onChange={onChange}
          tooltipProps={toggleTooltipProps} />
      </div>
      <div className={"flex-column flex-grow margin-left-16 padding-horizontal-16"}>
        <div className="flex-row flex-wrap">
          <div className="flex-column flex-grow">
            <h3 className="body-m margin-0 flex-row">
              <span className="icon-margin padding-vertical-4">{title}</span>
              {showHighRiskPill &&
                <Tooltip text={Resources.HighRiskToolTip}>
                  <Pill className="inline-flex-row font-weight-normal" variant={PillVariant.outlined} iconProps={{ iconName: "BlockedSite", className: "warning-text" }}>{Resources.HighRisk}</Pill>
                </Tooltip>
              }
            </h3>
            <div className="body-s">
              {showHighRiskTitle &&
                <span className="warning-text">
                  {highRiskTitle ? highRiskTitle : Resources.HighRiskAuditTitle}
                </span>
              }
              <div className="secondary-text">
                {description}
                {learnMoreLink ? (
                  <span>
                    {" "}
                    <Link onClick={onLearnMoreClicked} ariaLabel={title ? format(Resources.LearnMoreAbout, title) : undefined} target="_blank" href={learnMoreLink}>{Resources.LearnMore}</Link>
                  </span>
                ) : null}
              </div>
            </div>
            {showHighRiskAudit &&
              <div className="flex-column flex-grow margin-top-16">
                <div className="body-s">
                  <div className="primary-text">
                    {Resources.HighRiskAuditToolTip}
                  </div>
                </div>
                <div className="margin-top-4" style={auditCurrentWidth ? { maxWidth: auditCurrentWidth } : undefined}>
                  <Toggle
                    ariaLabel={format(Resources.HighRiskToggleFormat, Resources.HighRiskAuditTitle, auditChecked ? Resources.HighRiskAuditToggleOn : Resources.HighRiskAuditToggleOff)}
                    className={toggleClassName}
                    checked={auditChecked}
                    disabled={auditDisabled}
                    offText={Resources.HighRiskAuditToggleOff}
                    onText={Resources.HighRiskAuditToggleOn}
                    onChange={onAuditChange}
                    tooltipProps={toggleAuditTooltipProps} />
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
}
